import { mergeAttributes, Node } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';

export interface VariableOptions {
  HTMLAttributes: Record<string, any>;
}

export interface VariableLinkText {
  isVariable: boolean;
  text?: string; // for static text
  path?: string; // for variable text
  label?: string; // for variable label
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variable: {
      /**
       * Add a generic variable node (span)
       */
      insertVariable: (options: { path: string; format?: string; label: string }) => ReturnType;
      /**
       * Add a variable link node (anchor)
       */
      insertVariableLink: (options: {
        path: string;
        href: string;
        text: VariableLinkText;
        label: string;
      }) => ReturnType;
    };
  }
}

export const Variable = Node.create<VariableOptions>({
  name: 'variable',

  group: 'inline',

  inline: true,

  content: 'inline*', // Allow inline content, primarily for link text

  atom: true,

  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      'data-label': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => {
          if (!attributes['data-label']) {
            return {};
          }

          return { 'data-label': attributes['data-label'] };
        },
      },
      'data-from': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-from'),
        renderHTML: (attributes) => {
          if (!attributes['data-from']) {
            return {};
          }

          return { 'data-from': attributes['data-from'] };
        },
      },
      'data-format': {
        // For mentions
        default: null,
        parseHTML: (element) => element.getAttribute('data-format'),
        renderHTML: (attributes) => {
          if (!attributes['data-format']) {
            return {};
          }

          return { 'data-format': attributes['data-format'] };
        },
      },
      href: {
        // For links
        default: null,
        parseHTML: (element) => element.getAttribute('href'),
        renderHTML: (attributes) => {
          if (!attributes.href) {
            return {};
          }

          // Render href only if it's intended to be a link (logic in renderHTML decides tag)
          return { href: attributes.href }; // Actual href application needs tag context
        },
      },
      'data-is-link': {
        default: false,
        parseHTML: (element) => element.getAttribute('data-is-link'),
        renderHTML: (attributes) => {
          if (!attributes.href) {
            return {};
          }

          return { 'data-is-link': attributes['data-is-link'] };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="variable"]',
        priority: 100,
        getAttrs: (element: HTMLElement) => {
          const label = element.getAttribute('data-label');
          const path = element.getAttribute('data-from');

          if (!path) {
            return false;
          }

          return {
            'data-from': path,
            'data-label': label,
          };
        },
      },
      {
        // Link variables
        tag: 'a[data-type="variable"]',
        priority: 100,
        getAttrs: (element: HTMLElement) => {
          const label = element.getAttribute('data-label');
          const path = element.getAttribute('data-from');
          const href = element.getAttribute('href');

          if (!path || href === null) {
            return false;
          }

          return {
            'data-from': path,
            'data-label': label,
            'data-is-link': 'true',
            href: href,
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    let tag = 'span';
    // customRenderAttributes are used to ensure data-type is on the element
    // for both renderHTML and toHTML (if it were used).
    const customRenderAttributes: Record<string, any> = {
      'data-type': 'variable',
      'data-label': node.attrs['data-label'],
    };

    if (node.attrs.href) {
      tag = 'a';
    }

    const finalAttributes = mergeAttributes(
      this.options.HTMLAttributes,
      HTMLAttributes, // These are node.attrs passed by TipTap
      customRenderAttributes,
    );

    // The CSS ::before on this span shows the {{path}} tag.
    return [tag, finalAttributes, 0];
  },

  addNodeView() {
    return ({ node }) => {
      const span = document.createElement('span');

      span.classList.add('unthread-template-variable');

      if (node.attrs['data-is-link'] === 'true') {
        span.innerHTML = '🔗 ';
      }

      if (node.attrs['data-format'] === 'user-mention') {
        span.innerHTML += '👤 ';
      }

      if (node.attrs['data-format'] === 'slack-usergroup-mention') {
        span.innerHTML += '👥 ';
      }

      if (node.attrs['data-format'] === 'slack-channel-mention') {
        span.innerHTML += '💬 ';
      }

      span.innerHTML += node.attrs['data-label'] || node.attrs['data-from'];

      if (node.content.size > 0) {
        span.innerHTML += '|';

        const child = node.content.firstChild;

        if (child.attrs?.['data-label'] || child.attrs?.['data-from']) {
          span.innerHTML += child.attrs['data-label'] || child.attrs['data-from'];
        } else {
          span.innerHTML += child.textContent;
        }
      }

      return { dom: span };
    };
  },

  addCommands() {
    return {
      insertVariable:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              'data-from': options.path,
              'data-format': options.format || null,
              'data-label': options.label,
              href: null, // Ensure href is null for non-links
            },
            // Content is empty for non-link spans
            content: [],
          });
        },
      insertVariableLink:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              'data-from': options.path,
              'data-format': null, // Ensure format is null for links
              'data-is-link': 'true',
              'data-label': options.label,
              href: options.href,
            },
            // Set the link text as the node's content
            content: options.text.isVariable
              ? [
                  {
                    type: this.name, // Nested variable node
                    attrs: {
                      'data-from': options.text.path,
                      'data-format': null,
                      'data-label': options.text.label,
                      href: null,
                    },
                    content: [], // Nested simple variables have no content
                  },
                ]
              : options.text.text?.trim()
                ? [{ type: 'text', text: options.text.text }]
                : [], // Simple text node
          });
        },
    };
  },

  // Make variable nodes non-editable directly
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            // Prevent clicks inside the node from changing the editor selection
            mousedown: (_view, event) => {
              const target = event.target as HTMLElement;

              // Allow default behavior for actual links (variable or otherwise)
              if (target?.closest('a')) {
                return false; // Don't prevent default link interaction
              }

              // Prevent clicks inside variable spans from changing selection
              const variableSpan = target?.closest(`span[data-type="variable"]`);

              if (variableSpan) {
                return true; // Prevent default behavior
              }

              return false;
            },
          },
        },
      }),
    ];
  },
});
